import * as d3 from "d3";
import { scaleLinear } from "d3-scale";
import React, { useRef, useState, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import proj4 from "proj4";
import { handleSelectionChange } from "../utils/selectionUtils";
import { COLORS, getZimmermanLaydownColor, generateColorMapping, generateLockMapping } from "../utils/colorUtils";
export default function ZimmerBlock(props) {
  const {
    data,
    tableCoordinatesData,
    d3Size,
    pierLegends,
    progressColorData,
    lockUnlockData = {},
    currentProjectDetails,
    selectedRow,
    // Now using the new JSON structure
    setSelectedRow,
    // Function to update the new JSON structure
    type,
    userSelection,
    boolean
  } = props?.datas;

  // Extract data from the new structure
  const bulkPierData = data?.piers || {};
  const tableBays = data?.tt || {};
  let enableGPSLocation = boolean?.enableGPSLocation;
  let controls = boolean?.controls;
  let deviceType = type?.deviceType;
  let layoutType = type?.layoutType;
  let laydown = boolean?.laydown || false;
  let disableSelection = false;
  let currentLaydownData = 0;
  let laydownData = [];
  // Flatten pier data for operations that need an array
  const flatPierData = Object.entries(bulkPierData).flatMap(([trackerId, pierArr]) => pierArr.map(pier => ({
    ...pier,
    tracker_row_id: Number.parseInt(trackerId)
  })));
  const [position, setPosition] = useState([0, 0]);
  const [adjust, setAdjust] = useState({
    x: 0,
    y: 0
  });
  const containerRef = useRef();
  const downloadRef = useRef();
  const pierObjs = {};
  const getPrLegend = data => {
    const prlegend = pierLegends[data];
    if (pierLegends && prlegend) {
      return prlegend;
    } else {
      return {
        shape: "circle",
        concentric: false,
        border: "black",
        background: "white",
        width: 1,
        map_color: "#4287f5"
      };
    }
  };
  const handleSelectTable = async data => {
    const trackerId = Number.parseInt(data);
    if (laydown) {
      if (disableSelection === false) {
        if (tableCoordinatesData[data]?.laydown_id === null || tableCoordinatesData[data]?.laydown_id === currentLaydownData) {
          // Use direct access with key
          const pierArr = bulkPierData[trackerId];
          if (pierArr && pierArr.length > 0) {
            const temp = pierArr[0]; // Take the first element to get section, tracker_type_id, etc.

            // Use our utility to update selected rows
            const newSelectedRows = await handleSelectionChange("individualTracker", temp);
            setSelectedRow(newSelectedRows);
          }
        }
      }
    } else {
      // Use direct access with key
      const pierArr = bulkPierData[trackerId];
      if (pierArr && pierArr.length > 0) {
        const temp = {
          ...pierArr[0],
          tracker_row_id: trackerId
        }; // Take the first element to get section, tracker_type_id, etc.
        // Use our utility to update selected rows
        const newSelectedRows = await handleSelectionChange("individualTracker", temp);
        setSelectedRow(newSelectedRows);
      }
    }
  };
  // Using the utility function for laydown color
  const getLaydownColor = laydownId => {
    return getZimmermanLaydownColor(laydownId, currentLaydownData);
  };
  const sectionCallFn = async () => {
    try {
      if (flatPierData && flatPierData.length > 0) {
        const temp = new Set();
        for (const i of flatPierData) {
          temp.add(i.section);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };
  function isNumber(value) {
    return typeof value === 'number';
  }
  function partNumberSplit(data) {
    if (isNumber(data)) {
      return data;
    } else {
      const temp = data;
      return temp;
    }
  }
  const findD3Height = () => {
    const temp = new Set();
    let result;
    flatPierData.forEach(i => {
      temp.add(i?.section);
    });
    result = Array.from(temp).length * 26;
    return result;
  };
  const findD3Width = () => {
    const temp = new Set();
    let result;
    flatPierData.forEach(i => {
      temp.add(i.row);
    });
    result = Array.from(temp).length * 20;
    return result;
  };
  function getLaydownName(laydownId) {
    const data = laydownData?.find(o => o.id === laydownId);
    return data?.name;
  }
  useEffect(() => {
    if (enableGPSLocation === true) {
      const watchId = navigator.geolocation.watchPosition(pos => {
        const {
          latitude,
          longitude
        } = pos.coords;
        const [east, north] = proj4(`+proj=utm +zone=60 +north +ellps=WGS84 +datum=WGS84 +units=m +no_defs`).forward([longitude, latitude]);
        setPosition({
          latitude: east,
          longitude: north
        });
      }, err => {
        console.error(err);
      });
      return () => {
        navigator.geolocation.clearWatch(watchId);
      };
    } else {
      setPosition([0, 0]);
    }
  }, [enableGPSLocation]);
  useEffect(() => {
    sectionCallFn();
    // eslint-disable-next-line
  }, [bulkPierData]);
  useEffect(() => {
    if (Object.keys(bulkPierData).length > 0) {
      renderMap(flatPierData);
    }
    // eslint-disable-next-line
  }, [data, tableCoordinatesData, position, selectedRow, layoutType, userSelection, progressColorData, adjust]);
  const renderMap = renderData => {
    if (downloadRef.current) {
      downloadRef.current.innerHTML = "";
    }
    containerRef.current.innerHTML = "";
    const wrapper = document.getElementById("wrapper");

    // Generate the color mapping once for the entire rendering process
    const colorMapping = generateColorMapping(progressColorData, layoutType, userSelection, selectedRow);
    const lockMapping = layoutType === "QC" ? generateLockMapping(lockUnlockData, selectedRow) : {};
    // 1. Fix the isTrackerSelected function to directly check selectedRows
    const isTrackerSelected = trackerId => {
      // Check if this tracker is in the selectedRows
      if (!selectedRow || !selectedRow.sections) return false;

      // Look through all sections to find if this trackerId is selected anywhere
      return Object.values(selectedRow.sections).some(section => section.selectedRows && section.selectedRows[trackerId]);
    };
    const xScalerangeVal = 150;
    const xScale = scaleLinear().domain([d3Size?.piers?.minimum_latitude, d3Size?.piers?.maximum_latitude]).range([xScalerangeVal, wrapper.offsetWidth - xScalerangeVal]);
    const yScalerangeVal = 300;
    const yScale = scaleLinear().domain([d3Size?.piers?.maximum_longitude, d3Size?.piers?.minimum_longitude]).range([yScalerangeVal, wrapper.offsetHeight - yScalerangeVal]);
    for (const pierData of renderData) {
      const pierObj = pierObjs[`pier-${getPrLegend(partNumberSplit(pierData.part_number)).shape}-${getPrLegend(partNumberSplit(pierData.part_number)).concentric}-${partNumberSplit(pierData.part_number)}`];
      const pierDatas = {
        bha_sg_labels: pierData?.bha_sg_labels,
        block: pierData?.block,
        block_id: pierData?.block_id,
        id: pierData?.id,
        latitude: xScale(pierData?.latitude),
        longitude: yScale(pierData?.longitude),
        pier_part_order: pierData?.pier_part_order,
        row: pierData?.row,
        section: pierData?.section,
        torque_tube_label: pierData?.torque_tube_label,
        tracker_row_id: pierData?.tracker_row_id,
        pier_type_motor: pierData?.pier_type_motor,
        tracker_type_id: pierData?.tracker_type_id,
        label_name: pierData?.label_name ? pierData?.label_name : `${pierData?.part_number}_255-0-127`,
        stroke: getPrLegend(partNumberSplit(pierData?.part_number))?.border ? getPrLegend(partNumberSplit(pierData?.part_number))?.border : "black",
        fill: getPrLegend(partNumberSplit(pierData?.part_number))?.background ? getPrLegend(partNumberSplit(pierData?.part_number))?.background : "white",
        stroke_width: getPrLegend(partNumberSplit(pierData?.part_number))?.width ? getPrLegend(partNumberSplit(pierData?.part_number))?.width : 1,
        map_colour: getPrLegend(partNumberSplit(pierData?.part_number))?.map_colour ? getPrLegend(partNumberSplit(pierData?.part_number))?.map_colour : "#4287f5"
      };
      if (!pierObj) {
        pierObjs[`pier-${getPrLegend(partNumberSplit(pierData?.part_number))?.shape}-${getPrLegend(partNumberSplit(pierData?.part_number))?.concentric}-${partNumberSplit(pierData?.part_number)}`] = [pierDatas];
      }
      if (pierObj) {
        pierObjs[`pier-${getPrLegend(partNumberSplit(pierData?.part_number))?.shape}-${getPrLegend(partNumberSplit(pierData?.part_number))?.concentric}-${partNumberSplit(pierData?.part_number)}`] = pierObjs[`pier-${getPrLegend(partNumberSplit(pierData?.part_number))?.shape}-${getPrLegend(partNumberSplit(pierData?.part_number))?.concentric}-${partNumberSplit(pierData?.part_number)}`].concat(pierDatas);
      }
    }
    if (laydown === false) {
      for (const tt_trackerId in tableCoordinatesData) {
        const ttData = tableCoordinatesData[tt_trackerId]["table_coordinates"];
        const ttColorCode = tableCoordinatesData[tt_trackerId]["color_code"];
        const ttHatch = tableCoordinatesData[tt_trackerId]["hatch"];
        const trackerId = Number.parseInt(tt_trackerId);

        // Use direct access with key
        const pierArr = bulkPierData[trackerId];
        if (pierArr && pierArr.length > 0) {
          const temp = pierArr[0];
          // Get color from mapping or use default
          let fillColor = COLORS.DEFAULT;
          if (layoutType === "CP") {
            // Existing CP color logic
            const colorKey = userSelection.selection !== "All" ? `${trackerId}_${String(userSelection.selection).toLowerCase()}` : `${trackerId}_all`;
            fillColor = colorMapping[colorKey] ? colorMapping[colorKey]?.mainColor : COLORS.DEFAULT;
          } else if (layoutType === "QC" || layoutType === "Punchlist") {
            // Existing QC/Punchlist color logic
            const colorKey = `${trackerId}_${userSelection.selection}`;
            fillColor = colorMapping[colorKey] || COLORS.DEFAULT;
          }

          // Check if this tracker row is selected
          const isSelected = isTrackerSelected(trackerId);
          if (ttData && ttData.length === 4) {
            // First draw the table polygon
            d3.select(containerRef.current).append("path").attr("class", "testing-border cursor-pointer").style("cursor", "pointer").attr("d", `M${xScale(ttData[0].x)} ${yScale(ttData[0].y)} L${xScale(ttData[1].x)} ${yScale(ttData[1].y)} L${xScale(ttData[2].x)} ${yScale(ttData[2].y)} L${xScale(ttData[3].x)} ${yScale(ttData[3].y)}  Z`).attr("stroke", `rgb(${ttColorCode.split("-")[0]}, ${ttColorCode.split("-")[1]}, ${ttColorCode.split("-")[2]})`).attr("stroke-width", "1px").attr("fill", isSelected ? COLORS.SELECTED : fillColor).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Row-${temp?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTable(tt_trackerId));
            if (ttHatch) {
              d3.select(containerRef.current).append("line").attr("class", "testing-border-diag cursor-pointer").style("cursor", "pointer").attr("x1", xScale(ttData[0].x)).attr("y1", yScale(ttData[0].y)).attr("x2", xScale(ttData[2].x)).attr("y2", yScale(ttData[2].y)).attr("stroke", `rgb(${ttColorCode.split("-")[0]}, ${ttColorCode.split("-")[1]}, ${ttColorCode.split("-")[2]})`);
            }
            if (layoutType === "QC" && lockMapping[trackerId]) {
              // Find center of the quadrilateral
              const centerX = (xScale(ttData[0].x) + xScale(ttData[1].x) + xScale(ttData[2].x) + xScale(ttData[3].x)) / 4;
              const centerY = (yScale(ttData[0].y) + yScale(ttData[1].y) + yScale(ttData[2].y) + yScale(ttData[3].y)) / 4;

              // Use COLORS.LOCKED (blue) with low opacity
              const lowOpacity = 0.7; // Very low opacity for subtlety

              // Add lock icon without background circle
              const lockGroup = d3.select(containerRef.current).append("g").attr("class", "lock-icon-group").attr("transform", `translate(${centerX}, ${centerY})`);

              // Original sizes (after 30% reduction)
              const origX = -7;
              const origY = -3.5;
              const origWidth = 14;
              const origHeight = 12.6;
              const origRx = 2.1;
              const origRy = 2.1;
              const origStrokeWidth = 2.8;
              const origRadius = 2.1;

              // Apply additional 5% reduction
              const scale = 0.95; // 5% smaller

              // Lock body - now 5% smaller than before
              lockGroup.append("rect").attr("class", "lock-body").attr("x", origX * scale).attr("y", origY * scale).attr("width", origWidth * scale).attr("height", origHeight * scale).attr("rx", origRx * scale).attr("ry", origRy * scale).attr("fill", COLORS.LOCKED).attr("opacity", lowOpacity);

              // Lock shackle - now 5% smaller than before
              // For the path, we need to scale the d attribute
              lockGroup.append("path").attr("class", "lock-shackle").attr("d", `M ${-4.2 * scale} ${-3.5 * scale} C ${-4.2 * scale} ${-9.1 * scale}, ${4.2 * scale} ${-9.1 * scale}, ${4.2 * scale} ${-3.5 * scale}`).attr("stroke", COLORS.LOCKED).attr("stroke-width", origStrokeWidth * scale).attr("fill", "none").attr("opacity", lowOpacity);

              // Lock keyhole - now 5% smaller than before
              lockGroup.append("circle").attr("class", "lock-keyhole").attr("cx", 0).attr("cy", 2.1 * scale).attr("r", origRadius * scale).attr("fill", "white").attr("opacity", lowOpacity);
            }
          }
        }
      }
    } else {
      for (const tt_trackerId in tableCoordinatesData) {
        const ttData = tableCoordinatesData[tt_trackerId]["table_coordinates"];
        const ttColorCode = tableCoordinatesData[tt_trackerId]["color_code"];
        const ttHatch = tableCoordinatesData[tt_trackerId]["hatch"];
        const laydown_id = tableCoordinatesData[tt_trackerId]["laydown_id"];
        const trackerId = Number.parseInt(tt_trackerId);

        // Use direct access with key
        const pierArr = bulkPierData[trackerId];
        if (pierArr && pierArr.length > 0) {
          const temp = pierArr[0];

          // Check if this tracker row is selected using our utility
          const isSelected = isTrackerSelected(trackerId);
          if (ttData && ttData.length === 4) {
            d3.select(containerRef.current).append("path").attr("class", "testing-border cursor-pointer").style("cursor", "pointer").attr("d", `M${xScale(ttData[0].x)} ${yScale(ttData[0].y)} L${xScale(ttData[1].x)} ${yScale(ttData[1].y)} L${xScale(ttData[2].x)} ${yScale(ttData[2].y)} L${xScale(ttData[3].x)} ${yScale(ttData[3].y)}  Z`).attr("stroke", `rgb(${ttColorCode.split("-")[0]}, ${ttColorCode.split("-")[1]}, ${ttColorCode.split("-")[2]})`).attr("stroke-width", "1px").attr("fill", `${isSelected ? COLORS.SELECTED : getLaydownColor(laydown_id)}`).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(laydown ? getLaydownName(laydown_id) : `Row-${temp?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTable(tt_trackerId));
            if (ttHatch) {
              d3.select(containerRef.current).append("line").attr("class", "testing-border-diag cursor-pointer").style("cursor", "pointer").attr("x1", xScale(ttData[0].x)).attr("y1", yScale(ttData[0].y)).attr("x2", xScale(ttData[2].x)).attr("y2", yScale(ttData[2].y)).attr("stroke", `rgb(${ttColorCode.split("-")[0]}, ${ttColorCode.split("-")[1]}, ${ttColorCode.split("-")[2]})`);
            }
          }
        }
      }
    }
    // Similarly update the table bays rendering code:
    for (const tracker_row_id in tableBays) {
      const trackerBayData = tableBays[tracker_row_id];
      const trackerId = Number.parseInt(tracker_row_id);

      // Use direct access with key for first pier to get section and other properties
      const pierArr = bulkPierData[trackerId];
      if (pierArr && pierArr.length > 0) {
        // Check if this tracker is selected
        const isSelected = isTrackerSelected(trackerId);

        // Handle QC type for table bays - only for structure and module scopes
        // if ((layoutType === "QC" || layoutType === "Punchlist") && (userSelection.selection === "structure" || userSelection.selection === "module")) {

        //   // Draw bay with the pre-computed color
        //   trackerBayData?.forEach((temp) => {
        //     let colorKey = `${trackerId}_${temp?.id}_${userSelection.selection}`

        //     const qcColor = colorMapping[colorKey];
        //     const ttData = temp?.tt_window_zimmerman;
        //     if (qcColor) {
        //       if (ttData && ttData.length >= 6) {
        //         d3.select(containerRef.current)
        //           .append("path")
        //           .attr("class", "tableBays cursor-pointer")
        //           .attr("d", `M${xScale(ttData[0]?.latitude)} ${yScale(ttData[0]?.longitude)} L${xScale(ttData[1]?.latitude)} ${yScale(ttData[1]?.longitude)} L${xScale(ttData[3]?.latitude)} ${yScale(ttData[3]?.longitude)} L${xScale(ttData[5]?.latitude)} ${yScale(ttData[5]?.longitude)} L${xScale(ttData[4]?.latitude)} ${yScale(ttData[4]?.longitude)} L${xScale(ttData[2]?.latitude)} ${yScale(ttData[2]?.longitude)} Z`)
        //           .attr("stroke", qcColor) // Match fill color
        //           .attr("stroke-width", "0.5px")
        //           .attr("fill", isSelected ? COLORS.SELECTED : qcColor)
        //           .on("click", () => handleSelectTable(tracker_row_id));
        //       }
        //     }
        //   });
        // }
        if ((layoutType === "QC" || layoutType === "Punchlist") && (userSelection.selection === "structure" || userSelection.selection === "module")) {
          // Check for combined tracker color first
          let combinedColorKey = `${trackerId}_${userSelection.selection}`;
          const combinedColor = colorMapping[combinedColorKey];
          if (combinedColor && combinedColor !== COLORS.DEFAULT) {
            // Use first TT coordinates to draw entire table
            const firstTT = trackerBayData?.[0];
            if (firstTT && firstTT.tt_window_zimmerman && firstTT.tt_window_zimmerman.length >= 6) {
              const ttData = firstTT.tt_window_zimmerman;
              d3.select(containerRef.current).append("path").attr("class", "tableBays cursor-pointer").attr("d", `M${xScale(ttData[0]?.latitude)} ${yScale(ttData[0]?.longitude)} L${xScale(ttData[1]?.latitude)} ${yScale(ttData[1]?.longitude)} L${xScale(ttData[3]?.latitude)} ${yScale(ttData[3]?.longitude)} L${xScale(ttData[5]?.latitude)} ${yScale(ttData[5]?.longitude)} L${xScale(ttData[4]?.latitude)} ${yScale(ttData[4]?.longitude)} L${xScale(ttData[2]?.latitude)} ${yScale(ttData[2]?.longitude)} Z`).attr("stroke", combinedColor).attr("stroke-width", "0.5px").attr("fill", isSelected ? COLORS.SELECTED : combinedColor).on("click", () => handleSelectTable(tracker_row_id));
            }
          } else {
            // If no combined color, check individual TT colors
            trackerBayData?.forEach(temp => {
              let colorKey = `${trackerId}_${temp?.id}_${userSelection.selection}`;
              const qcColor = colorMapping[colorKey];
              const ttData = temp?.tt_window_zimmerman;
              if (qcColor && qcColor !== COLORS.DEFAULT) {
                if (ttData && ttData.length >= 6) {
                  d3.select(containerRef.current).append("path").attr("class", "tableBays cursor-pointer").attr("d", `M${xScale(ttData[0]?.latitude)} ${yScale(ttData[0]?.longitude)} L${xScale(ttData[1]?.latitude)} ${yScale(ttData[1]?.longitude)} L${xScale(ttData[3]?.latitude)} ${yScale(ttData[3]?.longitude)} L${xScale(ttData[5]?.latitude)} ${yScale(ttData[5]?.longitude)} L${xScale(ttData[4]?.latitude)} ${yScale(ttData[4]?.longitude)} L${xScale(ttData[2]?.latitude)} ${yScale(ttData[2]?.longitude)} Z`).attr("stroke", qcColor).attr("stroke-width", "0.5px").attr("fill", isSelected ? COLORS.SELECTED : qcColor).on("click", () => handleSelectTable(tracker_row_id));
                }
              }
            });
          }
        }
        // Original CP logic - using color mapping with exact keys
        else if (layoutType !== "QC" && layoutType !== "Punchlist") {
          let colorKey = "";
          if (layoutType === "CP") {
            colorKey = userSelection.selection !== "All" ? `${trackerId}_${String(userSelection.selection).toLowerCase()}` : `${trackerId}_all`;
          } else if (layoutType === "QC" || layoutType === "Punchlist") {
            colorKey = `${trackerId}_${userSelection.selection}`;
          }
          // Process individual torque tubes with in-progress status
          trackerBayData?.forEach(temp => {
            // For torque tubes, check if there's specific data in the progressColorData
            const currentActivityData = progressColorData[colorKey];

            // Find if this torque tube has in-progress status
            let ttHasInProgressStatus = false;
            if (currentActivityData && currentActivityData.torque_tubes) {
              const ttStatus = currentActivityData.torque_tubes.find(tt => tt.torque_tube_id === temp.id);
              ttHasInProgressStatus = ttStatus && ttStatus.status === 1;
            }
            if (ttHasInProgressStatus) {
              const ttData = temp?.tt_window_zimmerman;
              d3.select(containerRef.current).append("path").attr("class", "tableBays cursor-pointer").style("cursor", "pointer").attr("d", `M${xScale(ttData[0]?.latitude)} ${yScale(ttData[0]?.longitude)} L${xScale(ttData[1]?.latitude)} ${yScale(ttData[1]?.longitude)} L${xScale(ttData[3]?.latitude)} ${yScale(ttData[3]?.longitude)} L${xScale(ttData[5]?.latitude)} ${yScale(ttData[5]?.longitude)} L${xScale(ttData[4]?.latitude)} ${yScale(ttData[4]?.longitude)} L${xScale(ttData[2]?.latitude)} ${yScale(ttData[2]?.longitude)} Z`).attr("stroke", COLORS.COMPLETED) // Color for in-progress
              .attr("stroke-width", "0.5px").attr("fill", isSelected ? COLORS.SELECTED : COLORS.COMPLETED).on("click", () => handleSelectTable(tracker_row_id));
            }
          });
        }
      }
    }
    for (const pierData in pierObjs) {
      for (const data of pierObjs[pierData]) {
        const trackerId = data.tracker_row_id;
        const pierId = data.id;

        // Use color mapping for both QC and CP types
        if (layoutType === "QC" || layoutType === "Punchlist") {
          const scope = userSelection?.selection?.toLowerCase();
          const pierQCKey = `${trackerId}_${pierId}_${scope}`;

          // Get pre-computed QC color from mapping
          const qcColor = colorMapping[pierQCKey];

          // Check if selected using the tracker color
          const isSelected = isTrackerSelected(trackerId);
          if (qcColor && qcColor !== COLORS.DEFAULT && !isSelected && userSelection?.selection === "piers") {
            d3.select(containerRef.current).append("circle").attr("class", "cursor-pointer").style("cursor", "pointer").attr("cx", data.latitude).attr("cy", data.longitude - 1).attr("r", 5).attr("stroke", qcColor) // Match fill color
            .attr("stroke-width", "0.5px").attr("fill", qcColor).on("click", () => handleSelectTable(data?.tracker_row_id));
          }
        } else if (layoutType === "CP" && userSelection?.selection !== "All") {
          // For CP, use the pier-specific color from mapping
          const key = `${trackerId}_${String(userSelection.selection).toLowerCase()}`;
          const pierColor = colorMapping[key?.piers?.pierId];

          // Check if selected using the tracker color
          // const trackerKey = `tracker_${trackerId}_${String(userSelection.selection).toLowerCase()}`;
          // const isSelected = colorMapping[trackerKey] === "#F6BE00";

          if (pierColor === COLORS.IN_PROGRESS || pierColor === COLORS.COMPLETED) {
            d3.select(containerRef.current).append("circle").attr("class", "cursor-pointer").style("cursor", "pointer").attr("cx", data?.latitude).attr("cy", data?.longitude - 1).attr("r", 5).attr("stroke", pierColor).attr("stroke-width", "0.5px").attr("fill", pierColor).on("click", () => handleSelectTable(data?.tracker_row_id));
          }
        }

        // Always render the label for the pier (unchanged)
        const labelColor = "0-0-0";
        d3.select(containerRef.current).append("text").attr("fill", `rgb(${labelColor.split("-")[0]}, ${labelColor.split("-")[1]}, ${labelColor.split("-")[2]})`).attr("x", data?.latitude).attr("y", data?.longitude + 2).attr("text-anchor", "middle").style("font-size", "8px").style("font-weight", "700").style("cursor", "pointer").text(data?.label_name.split("_") ? data?.label_name.split("_")[0] : "").on("click", () => handleSelectTable(data?.tracker_row_id));
      }
    }
    if (deviceType !== "web" && enableGPSLocation) {
      d3.select(containerRef.current).append("circle").attr("fill", "#A8BEE2").attr("cx", xScale(position?.latitude)).attr("cy", yScale(position?.longitude)).attr("r", 9);
      d3.select(containerRef.current).append("circle").attr("fill", "white").attr("cx", xScale(position?.latitude)).attr("cy", yScale(position?.longitude)).attr("r", 5);
      d3.select(containerRef.current).append("circle").attr("fill", "#3A72B6").attr("cx", xScale(position?.latitude)).attr("cy", yScale(position?.longitude)).attr("r", 4);
    }
    const tooltip = d3.select(containerRef.current).append("text").attr("class", "tooltip").attr("fill", "white").style("pointer-events", "none");
  };
  if (deviceType === "web") {
    const transCompHeightVal = currentProjectDetails?.svg_dimension?.web?.web_d3_height + findD3Height();
    const transCompWidthVal = currentProjectDetails?.svg_dimension?.web?.web_d3_width + 0;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        position: "relative"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: "calc(100vh - 220px)"
      }
    }, /*#__PURE__*/React.createElement(TransformWrapper, {
      centerZoomedOut: true,
      minScale: Number.parseFloat(currentProjectDetails?.svg_dimension?.web?.web_d3_min_scale),
      initialScale: Number.parseFloat(currentProjectDetails?.svg_dimension?.web?.web_d3_initial_scale),
      initialPositionX: 50 // Center X position (percentage)
      ,
      initialPositionY: 50 // Center Y position (percentage)
      ,
      centerOnInit: true
    }, /*#__PURE__*/React.createElement(TransformComponent, {
      contentStyle: {
        height: `${transCompHeightVal + adjust.y}px`,
        width: `${transCompWidthVal + adjust.x}px`
      },
      wrapperStyle: {
        height: "100%",
        width: "100%"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: `${transCompHeightVal + adjust.y}px`,
        width: `${transCompWidthVal + adjust.x}px`
      },
      id: "wrapper"
    }, /*#__PURE__*/React.createElement("svg", {
      ref: containerRef,
      style: {
        width: '100%',
        height: `${transCompHeightVal}px`,
        cursor: "grab"
      }
    }))))), controls && /*#__PURE__*/React.createElement("div", {
      style: {
        left: 0,
        bottom: 1,
        backgroundColor: "transparent",
        position: "absolute",
        cursor: "pointer"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "33%",
        left: "33%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "Adjust"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "42%",
        left: "35%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "view"), /*#__PURE__*/React.createElement("svg", {
      version: "1.0",
      xmlns: "http://www.w3.org/2000/svg",
      width: "100.000000pt",
      height: "100.000000pt",
      viewBox: "0 0 120.000000 120.000000",
      preserveAspectRatio: "xMidYMid meet"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "0",
      y: "0",
      width: "120",
      height: "120",
      rx: "10",
      ry: "10",
      fill: "none"
    }), /*#__PURE__*/React.createElement("g", {
      transform: "translate(0.000000,104.000000) scale(0.100000,-0.100000)",
      fill: "#000000",
      stroke: "none"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "540",
      cy: "520",
      r: "200",
      fill: "#a0a0a0",
      stroke: "#000000",
      strokeWidth: "5",
      onClick: () => setAdjust({
        ...adjust,
        center: !adjust.center
      })
    }, /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "705",
      textAnchor: "middle",
      fontSize: "40",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "Adjust"), /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "745",
      textAnchor: "middle",
      fontSize: "30",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "View")), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M295 240 l-70 -70 30 -25 c90 -75 233 -112 360 -95 71 10 185 59 234\r 101 l25 20 -72 72 c-68 68 -73 71 -94 58 c-62 -40 -85 -46 -163 -46 c-69 0 -87\r 4 -129 28 c-27 15 -49 27 -50 27 c-1 0 -33 -32 -71 -70z"
    }), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M828 773 l-67 -68 25 -42 c46 -76 45 -210 -2 -279 l-25 -37 72 -73\r 72 -73 28 36 c106 132 127 324 54 488 -21 49 -70 115 -84 115 -3 0 -36 -30\r -73 -67z"
    }), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M438 995\r c-66 -16 -139 -52 -183 -89\r l-30 -25 70 -70 70 -71 60 31\r c53 27 68 30 131 27 52 -2 84 -10 121 -30\r l51 -26 68 69 c76 76 76 79 0 126\r c-96 61 -249 86 -358 58z"
    }), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M156 805 c-89 -116 -113 -296 -61 -445 15 -45 82 -150 94 -150 4 0\r 37 30 73 68 l67 67 -27 55 c-39 79 -38 172 2 253 l28 59 -63 64 c-35 35 -69\r 64 -75 64 -6 0 -24 -16 -38 -35z"
    })))), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "none"
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("svg", {
      ref: downloadRef,
      style: {
        height: `${transCompHeightVal}px`,
        width: `${transCompWidthVal}px`
      }
    }))));
  } else {
    const transCompHeight = findD3Height();
    const transCompWidth = findD3Width();
    const transCompHeightVal = currentProjectDetails?.svg_dimension?.tablet?.tab_d3_height / 2 + transCompHeight;
    const transCompWidthVal = (currentProjectDetails?.svg_dimension?.tablet?.tab_d3_width + 500) / 2 + transCompWidth;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        position: "relative"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: "calc(100vh - 180px)"
      }
    }, /*#__PURE__*/React.createElement(TransformWrapper, {
      minScale: Number.parseFloat(currentProjectDetails?.svg_dimension?.tablet?.tab_svg_min_scale)
    }, /*#__PURE__*/React.createElement(TransformComponent, {
      contentStyle: {
        height: `${transCompHeightVal + adjust.y}px`,
        width: `${transCompWidthVal + adjust.x}px`
      },
      wrapperStyle: {
        height: "100%",
        width: "95%"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: `${transCompHeightVal + adjust.y}px`,
        width: `${transCompWidthVal + adjust.x}px`
      },
      id: "wrapper"
    }, /*#__PURE__*/React.createElement("svg", {
      ref: containerRef,
      style: {
        height: `${transCompHeightVal}px`,
        width: `${transCompWidthVal}px`
      }
    }))))), controls && /*#__PURE__*/React.createElement("div", {
      style: {
        position: "absolute",
        left: 0,
        bottom: 10,
        backgroundColor: "transparent"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "33%",
        left: "33%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "Adjust"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "42%",
        left: "35%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "view"), /*#__PURE__*/React.createElement("svg", {
      version: "1.0",
      xmlns: "http://www.w3.org/2000/svg",
      width: "100.000000pt",
      height: "100.000000pt",
      viewBox: "0 0 120.000000 120.000000",
      preserveAspectRatio: "xMidYMid meet"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "0",
      y: "0",
      width: "120",
      height: "120",
      rx: "10",
      ry: "10",
      fill: "none"
    }), /*#__PURE__*/React.createElement("g", {
      transform: "translate(0.000000,104.000000) scale(0.100000,-0.100000)",
      fill: "#000000",
      stroke: "none"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "540",
      cy: "520",
      r: "200",
      fill: "#a0a0a0",
      stroke: "#000000",
      strokeWidth: "5",
      onClick: () => setAdjust({
        ...adjust,
        center: !adjust.center
      })
    }, /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "705",
      textAnchor: "middle",
      fontSize: "40",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "Adjust"), /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "745",
      textAnchor: "middle",
      fontSize: "30",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "View")), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M295 240 l-70 -70 30 -25 c90 -75 233 -112 360 -95 71 10 185 59 234\r 101 l25 20 -72 72 c-68 68 -73 71 -94 58 c-62 -40 -85 -46 -163 -46 c-69 0 -87\r 4 -129 28 c-27 15 -49 27 -50 27 c-1 0 -33 -32 -71 -70z"
    }), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M828 773 l-67 -68 25 -42 c46 -76 45 -210 -2 -279 l-25 -37 72 -73\r 72 -73 28 36 c106 132 127 324 54 488 -21 49 -70 115 -84 115 -3 0 -36 -30\r -73 -67z"
    }), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M438 995\r c-66 -16 -139 -52 -183 -89\r l-30 -25 70 -70 70 -71 60 31\r c53 27 68 30 131 27 52 -2 84 -10 121 -30\r l51 -26 68 69 c76 76 76 79 0 126\r c-96 61 -249 86 -358 58z"
    }), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M156 805 c-89 -116 -113 -296 -61 -445 15 -45 82 -150 94 -150 4 0\r 37 30 73 68 l67 67 -27 55 c-39 79 -38 172 2 253 l28 59 -63 64 c-35 35 -69\r 64 -75 64 -6 0 -24 -16 -38 -35z"
    })))));
  }
}